import React, { useEffect, useState } from "react";
import { Eye, Trash2, Mail, Users as UsersIcon, Search, KeyRound, Loader2, UserPlus, Shield, Briefcase, User as UserIcon, SlidersHorizontal, LogIn } from "lucide-react";
import { Input } from "../ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { Checkbox } from "../ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import { Button } from "../ui/button";
import { toast } from "sonner";
import Avatar from "../common/Avatar";
import { useAuth } from "../../context/AuthContext";
import { listUsers, deleteUserApi, adminSetUserPassword, createUserApi, updateUserAccess, impersonateUser, formatDate } from "../../lib/api";

const MODULES = [
  { key: "leads", label: "Leads" },
  { key: "clients", label: "Clients" },
  { key: "notes", label: "Notes" },
  { key: "invoices", label: "Invoices" },
  { key: "products", label: "Services" },
  { key: "messages", label: "Messages" },
  { key: "appointments", label: "Appointments" },
  { key: "documents", label: "User Documents" },
];

const ROLE_META = {
  admin: { label: "Admin", icon: Shield, cls: "bg-navy/10 text-navy" },
  consultant: { label: "Consultant", icon: Briefcase, cls: "bg-gold/20 text-gold-dark" },
  client: { label: "User", icon: UserIcon, cls: "bg-gray-100 text-gray-600" },
};

const emptyAdd = () => ({ name: "", email: "", password: "", role: "client", phone: "", modules: [] });

export default function Users() {
  const { token } = useAuth();
  const [users, setUsers] = useState([]);
  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [viewing, setViewing] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [pwdUser, setPwdUser] = useState(null);
  const [newPwd, setNewPwd] = useState("");
  const [savingPwd, setSavingPwd] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState(emptyAdd());
  const [saving, setSaving] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ role: "client", modules: [] });
  const [savingEdit, setSavingEdit] = useState(false);

  const refresh = () => {
    if (!token) return;
    listUsers(token).then(setUsers).catch(() => {});
  };
  useEffect(() => { refresh(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleModule = (key) =>
    setAddForm((f) => ({
      ...f,
      modules: f.modules.includes(key) ? f.modules.filter((m) => m !== key) : [...f.modules, key],
    }));

  const toggleEditModule = (key) =>
    setEditForm((f) => ({
      ...f,
      modules: f.modules.includes(key) ? f.modules.filter((m) => m !== key) : [...f.modules, key],
    }));

  const openEdit = (u) => {
    setEditForm({ role: u.role, modules: u.modules || [] });
    setEditUser(u);
  };

  const saveAccess = async () => {
    setSavingEdit(true);
    try {
      await updateUserAccess(token, editUser.id, {
        role: editForm.role,
        modules: editForm.role === "consultant" ? editForm.modules : [],
      });
      toast.success("Access updated");
      setEditUser(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update access");
    } finally {
      setSavingEdit(false);
    }
  };

  const saveNewUser = async () => {
    if (!addForm.name.trim() || !addForm.email.trim()) { toast.error("Name and email are required."); return; }
    if (addForm.password.length < 6) { toast.error("Password must be at least 6 characters."); return; }
    setSaving(true);
    try {
      await createUserApi(token, {
        name: addForm.name.trim(),
        email: addForm.email.trim(),
        password: addForm.password,
        role: addForm.role,
        phone: addForm.phone,
        modules: addForm.role === "consultant" ? addForm.modules : [],
      });
      toast.success("User account created");
      setShowAdd(false);
      setAddForm(emptyAdd());
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create user");
    } finally {
      setSaving(false);
    }
  };

  const filtered = users.filter(
    (u) =>
      (roleFilter === "all" || u.role === roleFilter) &&
      (u.name.toLowerCase().includes(query.toLowerCase()) ||
        u.email.toLowerCase().includes(query.toLowerCase()))
  );

  const roleCounts = {
    all: users.length,
    admin: users.filter((u) => u.role === "admin").length,
    consultant: users.filter((u) => u.role === "consultant").length,
    client: users.filter((u) => u.role === "client").length,
  };

  const handleImpersonate = async (u) => {
    try {
      const data = await impersonateUser(token, u.id);
      localStorage.setItem("rfl_user_token", data.token);
      toast.success(`Opening portal as ${u.name}`);
      window.open("/portal", "_blank", "noopener");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not impersonate this user");
    }
  };

  const confirmDelete = async () => {
    try {
      await deleteUserApi(token, deleteId);
      setDeleteId(null);
      refresh();
      toast.success("User account deleted");
    } catch (e) {
      toast.error("Could not delete user");
    }
  };

  const savePassword = async () => {
    if (newPwd.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }
    setSavingPwd(true);
    try {
      await adminSetUserPassword(token, pwdUser.id, newPwd);
      toast.success(`Password updated for ${pwdUser.name}`);
      setPwdUser(null);
      setNewPwd("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update password");
    } finally {
      setSavingPwd(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-2xl font-bold text-navy">Users</h1>
          <p className="text-gray-500 text-sm mt-0.5">
            {users.length} account(s) — admins, consultants & users
          </p>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto flex-wrap">
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-[150px] bg-white" data-testid="role-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All roles ({roleCounts.all})</SelectItem>
              <SelectItem value="admin">Admins ({roleCounts.admin})</SelectItem>
              <SelectItem value="consultant">Consultants ({roleCounts.consultant})</SelectItem>
              <SelectItem value="client">Users ({roleCounts.client})</SelectItem>
            </SelectContent>
          </Select>
          <div className="relative flex-1 sm:w-56">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search users..."
              className="pl-10 bg-white"
            />
          </div>
          <Button onClick={() => { setAddForm(emptyAdd()); setShowAdd(true); }} className="bg-navy hover:bg-navy-light text-white whitespace-nowrap" data-testid="add-user-button">
            <UserPlus className="h-4 w-4 mr-1.5" /> Add User
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-cream">
              <tr className="text-left text-gray-500">
                <th className="px-5 py-3.5 font-medium">User</th>
                <th className="px-5 py-3.5 font-medium hidden sm:table-cell">Email</th>
                <th className="px-5 py-3.5 font-medium">Role</th>
                <th className="px-5 py-3.5 font-medium hidden md:table-cell">Last active</th>
                <th className="px-5 py-3.5 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => (
                <tr key={u.id} className="border-t border-gray-50">
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <Avatar
                        name={u.name}
                        src={u.avatar}
                        className="h-10 w-10 rounded-full bg-navy text-gold text-sm font-semibold"
                      />
                      <div className="font-medium text-navy">{u.name}</div>
                    </div>
                  </td>
                  <td className="px-5 py-4 text-gray-600 hidden sm:table-cell">{u.email}</td>
                  <td className="px-5 py-4">
                    {(() => {
                      const m = ROLE_META[u.role] || ROLE_META.client;
                      const Icon = m.icon;
                      return (
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${m.cls}`} data-testid={`role-badge-${u.id}`}>
                          <Icon className="h-3 w-3" /> {m.label}
                          {u.role === "consultant" && (u.modules?.length > 0) && (
                            <span className="text-[10px] opacity-70">· {u.modules.length} module{u.modules.length > 1 ? "s" : ""}</span>
                          )}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="px-5 py-4 text-gray-500 hidden md:table-cell" data-testid={`last-active-${u.id}`}>
                    {u.last_login ? formatDate(u.last_login) : <span className="text-gray-300">Never</span>}
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => setViewing(u)} title="View" className="h-8 w-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-navy/5 hover:text-navy transition-colors" data-testid={`view-user-${u.id}`}>
                        <Eye className="h-4 w-4" />
                      </button>
                      {u.role !== "admin" && (
                        <button onClick={() => handleImpersonate(u)} title="Impersonate (open portal as this user)" className="h-8 w-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-green-50 hover:text-green-600 transition-colors" data-testid={`impersonate-user-${u.id}`}>
                          <LogIn className="h-4 w-4" />
                        </button>
                      )}
                      <button onClick={() => openEdit(u)} title="Edit access" className="h-8 w-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-navy/5 hover:text-navy transition-colors" data-testid={`edit-access-${u.id}`}>
                        <SlidersHorizontal className="h-4 w-4" />
                      </button>
                      <button onClick={() => { setPwdUser(u); setNewPwd(""); }} title="Reset password" className="h-8 w-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gold/15 hover:text-gold-dark transition-colors" data-testid={`reset-password-${u.id}`}>
                        <KeyRound className="h-4 w-4" />
                      </button>
                      <button onClick={() => setDeleteId(u.id)} title="Delete" className="h-8 w-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-500 transition-colors" data-testid={`delete-user-${u.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan="5" className="px-5 py-12 text-center text-gray-400">
                    <UsersIcon className="h-10 w-10 mx-auto mb-2 opacity-50" />
                    No registered users{query ? " match your search" : " yet"}.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add user dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="add-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-navy flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-gold-dark" /> Add User
            </DialogTitle>
          </DialogHeader>
          <div className="py-1 space-y-3">
            <div>
              <label className="text-xs font-semibold text-gray-500 mb-1 block">Full Name</label>
              <Input value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} placeholder="Jane Doe" data-testid="add-user-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-gray-500 mb-1 block">Email</label>
                <Input type="email" value={addForm.email} onChange={(e) => setAddForm({ ...addForm, email: e.target.value })} placeholder="jane@email.com" data-testid="add-user-email" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 mb-1 block">Temp Password</label>
                <Input type="text" value={addForm.password} onChange={(e) => setAddForm({ ...addForm, password: e.target.value })} placeholder="min. 6 chars" data-testid="add-user-password" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 mb-1 block">Role</label>
              <Select value={addForm.role} onValueChange={(v) => setAddForm({ ...addForm, role: v })}>
                <SelectTrigger data-testid="add-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="client">User — client portal only</SelectItem>
                  <SelectItem value="consultant">Consultant — limited CRM access</SelectItem>
                  <SelectItem value="admin">Admin — full access</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {addForm.role === "consultant" && (
              <div data-testid="add-user-modules">
                <label className="text-xs font-semibold text-gray-500 mb-1.5 block">Module access</label>
                <div className="grid grid-cols-2 gap-2 bg-cream rounded-lg p-3">
                  {MODULES.map((m) => (
                    <label key={m.key} className="flex items-center gap-2 text-sm text-navy cursor-pointer" data-testid={`module-${m.key}`}>
                      <Checkbox checked={addForm.modules.includes(m.key)} onCheckedChange={() => toggleModule(m.key)} />
                      {m.label}
                    </label>
                  ))}
                </div>
                <p className="text-[11px] text-gray-400 mt-1.5">Dashboard is always available. Users & Settings stay admin-only.</p>
              </div>
            )}
            {addForm.role === "admin" && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5">Admins have full access to every module, including Users and Settings.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button onClick={saveNewUser} disabled={saving} className="bg-navy hover:bg-navy-light text-white" data-testid="add-user-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create User"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit access dialog */}
      <Dialog open={!!editUser} onOpenChange={(o) => !o && setEditUser(null)}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="edit-access-dialog">
          {editUser && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-navy flex items-center gap-2">
                  <SlidersHorizontal className="h-5 w-5 text-gold-dark" /> Edit Access
                </DialogTitle>
              </DialogHeader>
              <div className="py-1 space-y-3">
                <p className="text-sm text-gray-500">
                  Update the role and module access for <span className="font-medium text-navy">{editUser.name}</span> ({editUser.email}).
                </p>
                <div>
                  <label className="text-xs font-semibold text-gray-500 mb-1 block">Role</label>
                  <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}>
                    <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="client">User — client portal only</SelectItem>
                      <SelectItem value="consultant">Consultant — limited CRM access</SelectItem>
                      <SelectItem value="admin">Admin — full access</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {editForm.role === "consultant" && (
                  <div data-testid="edit-user-modules">
                    <label className="text-xs font-semibold text-gray-500 mb-1.5 block">Module access</label>
                    <div className="grid grid-cols-2 gap-2 bg-cream rounded-lg p-3">
                      {MODULES.map((m) => (
                        <label key={m.key} className="flex items-center gap-2 text-sm text-navy cursor-pointer" data-testid={`edit-module-${m.key}`}>
                          <Checkbox checked={editForm.modules.includes(m.key)} onCheckedChange={() => toggleEditModule(m.key)} />
                          {m.label}
                        </label>
                      ))}
                    </div>
                    <p className="text-[11px] text-gray-400 mt-1.5">Dashboard is always available. Users & Settings stay admin-only.</p>
                  </div>
                )}
                {editForm.role === "admin" && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5">Admins have full access to every module, including Users and Settings.</p>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditUser(null)}>Cancel</Button>
                <Button onClick={saveAccess} disabled={savingEdit} className="bg-navy hover:bg-navy-light text-white" data-testid="edit-access-save">
                  {savingEdit ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Access"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* View user dialog */}
      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="sm:max-w-sm">
          {viewing && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-navy">User Details</DialogTitle>
              </DialogHeader>
              <div className="py-2 flex flex-col items-center text-center">
                <Avatar
                  name={viewing.name}
                  src={viewing.avatar}
                  className="h-20 w-20 rounded-2xl bg-navy text-gold text-2xl font-serif font-bold"
                />
                <h3 className="mt-3 font-serif text-xl font-bold text-navy">{viewing.name}</h3>
                <a href={`mailto:${viewing.email}`} className="mt-1 inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-navy">
                  <Mail className="h-4 w-4" /> {viewing.email}
                </a>
                <div className="mt-4 w-full bg-cream rounded-lg px-4 py-3 text-left">
                  <div className="text-xs uppercase tracking-wide text-gray-400">Account ID</div>
                  <div className="text-sm font-mono text-navy break-all">{viewing.id}</div>
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => { const id = viewing.id; setViewing(null); setDeleteId(id); }}
                  className="text-red-600 hover:text-red-600 hover:bg-red-50 border-red-200"
                >
                  <Trash2 className="h-4 w-4 mr-1.5" /> Delete
                </Button>
                <Button onClick={() => setViewing(null)} className="bg-navy hover:bg-navy-light text-white">Close</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={!!pwdUser} onOpenChange={(o) => { if (!o) { setPwdUser(null); setNewPwd(""); } }}>
        <DialogContent className="sm:max-w-sm" data-testid="reset-password-dialog">
          {pwdUser && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-navy">Reset Password</DialogTitle>
              </DialogHeader>
              <div className="py-2 space-y-3">
                <p className="text-sm text-gray-500">
                  Set a new password for <span className="font-medium text-navy">{pwdUser.name}</span> ({pwdUser.email}).
                </p>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="text"
                    value={newPwd}
                    onChange={(e) => setNewPwd(e.target.value)}
                    placeholder="New password (min. 6 characters)"
                    className="pl-10"
                    data-testid="reset-password-dialog-input"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setPwdUser(null); setNewPwd(""); }}>Cancel</Button>
                <Button onClick={savePassword} disabled={savingPwd} className="bg-navy hover:bg-navy-light text-white" data-testid="reset-password-dialog-save">
                  {savingPwd ? <Loader2 className="h-4 w-4 animate-spin" /> : "Set Password"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this user account?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the user's website account. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-500 hover:bg-red-600">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
